"use client";

import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { BINOMIAL_TEST_CALCULATOR_STRINGS } from "@/components/tools/binomial-test-calculator";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import { t } from "@/lib/i18n/interpolate";
import { binomialUpper } from "@/lib/stats/discrete-tails";
import { binomialCdf, binomialPmf } from "@/lib/stats/distributions";
import {
  apaP,
  fixed,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";
import type { BinomialTestResult } from "@/lib/tool-kit/stats-tests-more/binomial-test";

/** Results for the binomial test calculator. */

type Strings = typeof BINOMIAL_TEST_CALCULATOR_STRINGS;

const WINDOW = 6;
const MAX_ROWS = 30;

/** Rows of the null distribution around the mode and the observed count. */
function tableRange(r: BinomialTestResult): { from: number; to: number } {
  const mode = Math.min(r.trials, r.expected);
  let from = Math.max(0, Math.min(r.successes, mode) - WINDOW);
  let to = Math.min(r.trials, Math.max(r.successes, mode) + WINDOW);
  if (to - from + 1 > MAX_ROWS) {
    // Far apart: show the observed count's neighbourhood and the mode's.
    const centre = Math.round((r.successes + mode) / 2);
    from = Math.max(0, centre - Math.floor(MAX_ROWS / 2));
    to = Math.min(r.trials, from + MAX_ROWS - 1);
  }
  return { from, to };
}

function Bar({ share }: { share: number }) {
  return (
    <div
      aria-hidden="true"
      className="h-3 rounded-sm bg-primary/60"
      style={{ width: `${Math.max(1, Math.round(share * 100))}%` }}
    />
  );
}

export function BinomialTestResults({
  r,
  alpha,
  strings,
  fmt,
}: {
  r: BinomialTestResult;
  alpha: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const reject = r.p < alpha;
  const levelText = fmt(r.level, {
    style: "percent",
    maximumFractionDigits: 1,
  });
  const { from, to } = tableRange(r);
  const rowValues = Array.from({ length: to - from + 1 }, (_, i) => from + i);
  const peak = Math.max(
    ...rowValues.map((k) => binomialPmf(k, r.trials, r.p0))
  );
  const region = r.twoSidedRegion;
  const counted = (k: number) =>
    region !== null && (k <= region.low || k >= region.high);
  const pk = binomialPmf(r.successes, r.trials, r.p0);
  const apa = richTemplate(strings.apaTemplate, {
    k: fmt(r.successes),
    n: fmt(r.trials),
    phat: fixed(r.pHat, 2, fmt, false),
    p0: fixed(r.p0, 2, fmt, false),
    p: apaP(r.p, fmt),
    level: levelText,
    lo: fixed(r.ci[0], 2, fmt, false),
    hi: fixed(r.ci[1], 2, fmt, false),
  });
  const oneSided = r.alternative !== "two-sided";
  const lessWorking = t(strings.workingLess, {
    k: fmt(r.successes),
    n: fmt(r.trials),
    p0: n(r.p0),
    p: formatP(r.pLess, fmt),
  });
  const greaterWorking = t(strings.workingGreater, {
    k: fmt(r.successes),
    n: fmt(r.trials),
    p0: n(r.p0),
    p: formatP(r.pGreater, fmt),
  });
  const twoWorking =
    region === null
      ? t(strings.workingTwoAll, { p: formatP(r.pTwoSided, fmt) })
      : t(strings.workingTwo, {
          low: fmt(region.low),
          high: fmt(region.high),
          k: fmt(r.successes),
          pk: formatStat(pk, fmt, 6),
          p: formatP(r.pTwoSided, fmt),
        });
  const regionParts: string[] = [];
  if (region && region.low >= 0) {
    regionParts.push(
      t(strings.regionLow, {
        low: fmt(region.low),
        p: formatP(binomialCdf(region.low, r.trials, r.p0), fmt),
      })
    );
  }
  if (region && region.high <= r.trials) {
    regionParts.push(
      t(strings.regionHigh, {
        high: fmt(region.high),
        p: formatP(binomialUpper(region.high, r.trials, r.p0), fmt),
      })
    );
  }
  const regionText =
    regionParts.length === 2
      ? t(strings.regionJoin, { a: regionParts[0], b: regionParts[1] })
      : regionParts.join("");
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.pValue} value={formatP(r.p, fmt)} />
        <StatCard label={strings.proportion} value={n(r.pHat)} />
        <StatCard
          label={strings.counts}
          value={`${fmt(r.successes)} / ${fmt(r.trials)}`}
        />
        <StatCard
          emphasis
          label={t(strings.ciLabel, { level: levelText })}
          value={`${n(r.ci[0])} – ${n(r.ci[1])}`}
        />
        <StatCard label={strings.cohenH} value={n(r.h)} />
        <StatCard label={strings.expected} value={fmt(r.expected)} />
      </StatGrid>
      <Decision reject={reject}>
        {t(reject ? strings.decisionReject : strings.decisionKeep, {
          alpha: fmt(alpha),
          p: formatP(r.p, fmt),
          p0: n(r.p0),
        })}
      </Decision>
      <h3 className={WIDGET_H3}>{strings.pValuesHeading}</h3>
      <ResultTable
        head={[strings.alternative, strings.p]}
        rows={[
          { key: "two", cells: [strings.twoSided, formatP(r.pTwoSided, fmt)] },
          { key: "less", cells: [strings.less, formatP(r.pLess, fmt)] },
          {
            key: "greater",
            cells: [strings.greater, formatP(r.pGreater, fmt)],
          },
        ]}
      />
      <h3 className={WIDGET_H3}>{strings.intervalsHeading}</h3>
      <div className="space-y-2 text-foreground text-sm">
        <p>
          {t(oneSided ? strings.clopperOneSided : strings.clopper, {
            lo: n(r.ci[0]),
            hi: n(r.ci[1]),
          })}
        </p>
        <p>{t(strings.wilson, { lo: n(r.wilson[0]), hi: n(r.wilson[1]) })}</p>
        <p className={HINT}>{strings.intervalHint}</p>
      </div>
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <p className={HINT}>{t(strings.tableHint, { k: fmt(r.successes) })}</p>
      {regionText ? (
        <p className="text-foreground text-sm">
          {t(strings.regionPrefix, { parts: regionText })}
        </p>
      ) : null}
      <ResultTable
        head={[
          strings.outcome,
          strings.pmf,
          strings.lower,
          strings.upper,
          strings.bar,
          strings.counted,
        ]}
        rows={rowValues.map((k) => {
          const pmf = binomialPmf(k, r.trials, r.p0);
          return {
            key: `k-${k}`,
            cells: [
              k === r.successes ? `${fmt(k)} (${strings.observed})` : fmt(k),
              formatStat(pmf, fmt, 6),
              formatStat(binomialCdf(k, r.trials, r.p0), fmt, 6),
              formatStat(binomialUpper(k, r.trials, r.p0), fmt, 6),
              <Bar key={`bar-${k}`} share={pmf / peak} />,
              counted(k) ? strings.yes : strings.no,
            ],
          };
        })}
      />
      <Working heading={strings.howCalculated}>
        <p>{lessWorking}</p>
        <p>{greaterWorking}</p>
        <p>{twoWorking}</p>
        <p>{strings.smallExpected}</p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={strings.apaNote}
        parts={apa}
      />
    </section>
  );
}
